import { cn } from "@/lib/cn";
import { clampPercent } from "@/lib/format";
import { AuraIcon } from "@/components/experimental/aura";
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";

type ButtonVariant =
  | "primary"
  | "accent"
  | "secondary"
  | "outline"
  | "onDark"
  | "ghost"
  | "danger";
type ButtonSize = "sm" | "md" | "lg" | "icon";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--brand-primary)] text-white shadow-[var(--shadow-brand)] hover:bg-[var(--brand-primary-strong)] disabled:opacity-50 disabled:shadow-none",
  accent:
    "bg-[var(--brand-accent)] text-white shadow-[var(--shadow-accent)] hover:brightness-110 disabled:opacity-50 disabled:shadow-none",
  secondary:
    "bg-[var(--brand-secondary)] text-white shadow-[var(--shadow-sm)] hover:brightness-110 disabled:opacity-50",
  outline:
    "border border-border-strong bg-surface text-text hover:border-[var(--brand-primary)] hover:bg-surface-muted disabled:opacity-50",
  onDark:
    "border border-white/30 bg-white/10 text-white backdrop-blur-sm hover:border-white/60 hover:bg-white/20 disabled:opacity-50",
  ghost: "text-text-muted hover:bg-surface-muted hover:text-text disabled:opacity-50",
  danger:
    "bg-[var(--danger)] text-white shadow-[var(--shadow-sm)] hover:brightness-110 disabled:opacity-50",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-9 px-3.5 text-[13px] gap-1.5 rounded-lg",
  md: "h-11 px-5 text-sm gap-2 rounded-xl",
  lg: "h-13 px-7 text-base gap-2.5 rounded-xl",
  icon: "h-11 w-11 justify-center rounded-xl",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center font-semibold tracking-tight transition-all duration-200 ease-out",
        "active:scale-[0.97] disabled:pointer-events-none",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
}

export function Card({
  className,
  interactive = false,
  ...props
}: HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-surface shadow-[var(--shadow-sm)]",
        interactive &&
          "transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-border-strong hover:shadow-[var(--shadow-md)]",
        className,
      )}
      {...props}
    />
  );
}

/** Card with the brand gradient — for hero and celebratory panels. */
export function GradientCard({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "brand-gradient relative overflow-hidden rounded-2xl text-white shadow-[var(--shadow-lg)]",
        className,
      )}
      {...props}
    />
  );
}

type PillTone =
  | "neutral"
  | "brand"
  | "accent"
  | "success"
  | "warning"
  | "danger"
  | "info";

const pillTones: Record<PillTone, string> = {
  neutral: "bg-surface-muted text-text-muted",
  brand: "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]",
  accent: "bg-[var(--brand-accent)]/12 text-[var(--brand-accent)]",
  success: "bg-[var(--success-soft)] text-[var(--success)]",
  warning: "bg-[var(--warning-soft)] text-[var(--warning)]",
  danger: "bg-[var(--danger-soft)] text-[var(--danger)]",
  info: "bg-[var(--info-soft)] text-[var(--info)]",
};

export function Pill({
  className,
  tone,
  children,
}: {
  className?: string;
  tone?: PillTone;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide",
        tone && pillTones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn("animate-spin text-current", className)}
      viewBox="0 0 24 24"
      fill="none"
      role="status"
      aria-label="Loading"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"
      />
    </svg>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("shimmer rounded-lg bg-surface-muted", className)}
      aria-hidden="true"
    />
  );
}

export function ProgressBar({
  percent,
  className,
  label,
  showValue = false,
}: {
  percent: number;
  className?: string;
  label?: string;
  showValue?: boolean;
}) {
  const value = clampPercent(percent);
  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          "h-2.5 w-full overflow-hidden rounded-full bg-surface-muted",
          className,
        )}
        role="progressbar"
        aria-valuenow={Math.round(value)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? "Progress"}
      >
        <div
          className="accent-gradient h-full rounded-full transition-[width] duration-700 ease-out"
          style={{ width: `${value}%` }}
        />
      </div>
      {showValue && (
        <span className="shrink-0 text-sm font-bold tabular-nums text-text">
          {Math.round(value)}%
        </span>
      )}
    </div>
  );
}

/**
 * Circular progress — the primary way challenge completion is shown, since a
 * ring reads faster than a bar at a glance on mobile.
 */
export function ProgressRing({
  percent,
  size = 140,
  strokeWidth = 12,
  label,
  children,
}: {
  percent: number;
  size?: number;
  strokeWidth?: number;
  label?: string;
  children?: ReactNode;
}) {
  const value = clampPercent(percent);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? "Progress"}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <defs>
          <linearGradient id="ring-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--brand-primary)" />
            <stop offset="100%" stopColor="var(--brand-accent)" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--surface-muted)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#ring-gradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 900ms var(--ease-out)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children}
      </div>
    </div>
  );
}

export function StatTile({
  label,
  value,
  hint,
  icon,
  tone = "neutral",
  layout = "horizontal",
  className,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  icon?: ReactNode;
  tone?: "neutral" | "brand" | "accent" | "success";
  layout?: "stacked" | "horizontal";
  className?: string;
}) {
  const iconTones = {
    neutral: "bg-surface-muted text-text-muted",
    brand: "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]",
    accent: "bg-[var(--brand-accent)]/12 text-[var(--brand-accent)]",
    success: "bg-[var(--success-soft)] text-[var(--success)]",
  } as const;

  return (
    <div
      className={cn(
        "group rounded-2xl border border-border bg-surface p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)] sm:p-5",
        layout === "horizontal" && "flex items-center gap-3.5 sm:p-4",
        className,
      )}
    >
      {icon && (
        <span
          className={cn(
            "mb-3 flex h-10 w-10 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110",
            layout === "horizontal" && "mb-0 shrink-0",
            iconTones[tone],
          )}
        >
          {icon}
        </span>
      )}
      <div className="min-w-0">
        <div className="text-[26px] font-extrabold leading-none tracking-tight text-text tabular-nums">
          {value}
        </div>
        <div className="mt-1.5 text-[13px] font-semibold text-text-muted">
          {label}
        </div>
        {hint && <div className="mt-1 text-xs text-text-subtle">{hint}</div>}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border-strong bg-surface px-6 py-16 text-center">
      {icon && (
        <span className="mb-4">
          <AuraIcon>{icon}</AuraIcon>
        </span>
      )}
      <h3 className="text-lg font-bold text-text">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-md text-sm text-text-muted">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function SectionHeading({
  title,
  subtitle,
  action,
  eyebrow,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  eyebrow?: string;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        {eyebrow && (
          <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--brand-accent)]">
            {eyebrow}
          </p>
        )}
        <h2 className="text-xl font-extrabold tracking-tight text-text">
          {title}
        </h2>
        {subtitle && <p className="mt-1 text-sm text-text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
